import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, AlertTriangle, CheckCircle, Info, TrendingUp, TrendingDown, Clock, Mail, MessageCircle, Award, FileBarChart, UserPlus, Settings as SettingsIcon, X, Check, Archive, Filter, Search, Video, Send, ThumbsUp, Star, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type AlertType = 'urgent' | 'warning' | 'info' | 'success';
type AlertCategory = 'learner' | 'system' | 'achievement' | 'report';
interface Alert {
  id: number;
  type: AlertType;
  category: AlertCategory;
  title: string;
  message: string;
  timestamp: string;
  date: Date;
  read: boolean;
  actionable: boolean;
  completed?: boolean;
  deleted?: boolean;
  learnerName?: string;
  learnerEmail?: string;
  module?: string;
}
export function AlertsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<'all' | AlertType>('all');
  const [selectedCategory, setSelectedCategory] = useState<'all' | AlertCategory | 'completed' | 'deleted'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'unread' | 'all'>('all');
  const [dateRange, setDateRange] = useState<'all' | 'today' | '7days' | '30days'>('all');
  const [activeCard, setActiveCard] = useState<'total' | 'urgent' | 'completed' | 'deleted' | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([{
    id: 1,
    type: 'urgent',
    category: 'learner',
    title: 'Learner Struggling - Immediate Attention Required',
    message: 'John Smith has failed JavaScript Basics 3 times with declining scores (68% → 65% → 58%). High misinformation rate detected.',
    timestamp: '5 minutes ago',
    date: new Date(Date.now() - 5 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'John Smith',
    learnerEmail: 'john.smith@example.com',
    module: 'JavaScript Basics'
  }, {
    id: 2,
    type: 'urgent',
    category: 'learner',
    title: 'At-Risk Learner - No Activity for 7 Days',
    message: 'Michael Chen has been inactive for 7 days. Last assessment score was 62% in CSS Fundamentals.',
    timestamp: '1 hour ago',
    date: new Date(Date.now() - 60 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'Michael Chen',
    learnerEmail: 'mchen@example.com',
    module: 'CSS Fundamentals'
  }, {
    id: 6,
    type: 'urgent',
    category: 'learner',
    title: 'CSS Fundamentals has 68% failure rate across all learners',
    message: '42 of 62 learners (68%) are failing CSS Fundamentals on first attempt. Average score is 54%, well below the 70% passing threshold. Common struggle areas: Flexbox (82% error rate), Grid Layout (76% error rate), and Positioning (71% error rate).',
    timestamp: '30 minutes ago',
    date: new Date(Date.now() - 30 * 60 * 1000),
    read: false,
    actionable: true,
    module: 'CSS Fundamentals'
  }, {
    id: 3,
    type: 'success',
    category: 'achievement',
    title: 'Outstanding Performance - Recognition Opportunity',
    message: 'Sarah Johnson achieved 95%+ on all 4 assessments on first attempt. Completed course 2 weeks ahead of schedule.',
    timestamp: '2 hours ago',
    date: new Date(Date.now() - 2 * 60 * 60 * 1000),
    read: false,
    actionable: true,
    learnerName: 'Sarah Johnson',
    learnerEmail: 'sarah.j@example.com',
    module: 'All Modules'
  }, {
    id: 4,
    type: 'warning',
    category: 'learner',
    title: 'Engagement Pattern Alert',
    message: 'Lisa Wong is taking unusually long between attempts (10+ days). Average score is 75% but engagement suggests time management issues.',
    timestamp: '5 hours ago',
    date: new Date(Date.now() - 5 * 60 * 60 * 1000),
    read: true,
    actionable: true,
    learnerName: 'Lisa Wong',
    learnerEmail: 'lisa.w@example.com',
    module: 'Responsive Design'
  }, {
    id: 5,
    type: 'info',
    category: 'system',
    title: 'Monthly Analytics Report Generated',
    message: 'Your monthly analytics report for December 2024 is ready for download.',
    timestamp: '1 day ago',
    date: new Date(Date.now() - 24 * 60 * 60 * 1000),
    read: true,
    actionable: false
  }, {
    id: 7,
    type: 'info',
    category: 'system',
    title: 'Scheduled Platform Maintenance',
    message: 'Platform maintenance is scheduled for Saturday, January 18th from 2:00 AM – 6:00 AM EST. During this window, assessments and reporting features will be temporarily unavailable. Please plan accordingly.',
    timestamp: '3 hours ago',
    date: new Date(Date.now() - 3 * 60 * 60 * 1000),
    read: false,
    actionable: false
  }]);
  // Modal states
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const [meetingModal, setMeetingModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const [kudosModal, setKudosModal] = useState<{
    isOpen: boolean;
    alert: Alert | null;
  }>({
    isOpen: false,
    alert: null
  });
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const markAsRead = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      read: true
    } : alert));
  };
  const markAllAsRead = () => {
    setAlerts(alerts.map(alert => alert.category === 'system' ? {
      ...alert,
      read: true
    } : {
      ...alert,
      completed: true,
      read: true
    }));
  };
  const completeAlert = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      completed: true,
      read: true
    } : alert));
  };
  const softDeleteAlert = (id: number) => {
    setAlerts(alerts.map(alert => alert.id === id ? {
      ...alert,
      deleted: true,
      read: true
    } : alert));
  };
  const deleteAlert = (id: number) => {
    setAlerts(alerts.filter(alert => alert.id !== id));
  };
  const filteredAlerts = alerts.filter(alert => {
    // Handle category-based status filters (Completed/Deleted from dropdown)
    if (selectedCategory === 'completed') {
      if (!alert.completed) return false;
    } else if (selectedCategory === 'deleted') {
      if (!alert.deleted) return false;
    } else if (activeCard === 'completed') {
      if (!alert.completed) return false;
    } else if (activeCard === 'deleted') {
      if (!alert.deleted) return false;
    } else {
      // Hide completed and deleted alerts from normal views
      if (alert.completed || alert.deleted) return false;
    }
    // Filter by read status based on view mode
    if (viewMode === 'unread' && alert.read) return false;
    const matchesType = selectedFilter === 'all' || alert.type === selectedFilter;
    const matchesCategory = selectedCategory === 'all' || selectedCategory === 'completed' || selectedCategory === 'deleted' || alert.category === selectedCategory;
    const matchesSearch = alert.title.toLowerCase().includes(searchQuery.toLowerCase()) || alert.message.toLowerCase().includes(searchQuery.toLowerCase()) || alert.learnerName && alert.learnerName.toLowerCase().includes(searchQuery.toLowerCase());
    // Date range filter
    let matchesDate = true;
    if (dateRange !== 'all') {
      const now = new Date();
      const alertDate = alert.date;
      if (dateRange === 'today') {
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        matchesDate = alertDate >= startOfDay;
      } else if (dateRange === '7days') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        matchesDate = alertDate >= sevenDaysAgo;
      } else if (dateRange === '30days') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        matchesDate = alertDate >= thirtyDaysAgo;
      }
    }
    return matchesType && matchesCategory && matchesSearch && matchesDate;
  });
  const unreadCount = alerts.filter(a => !a.read && !a.completed && !a.deleted).length;
  const readCount = alerts.filter(a => a.read).length;
  const urgentCount = alerts.filter(a => a.type === 'urgent' && !a.read && !a.completed && !a.deleted).length;
  const completedCount = alerts.filter(a => a.completed).length;
  const deletedCount = alerts.filter(a => a.deleted).length;
  const resetFilters = () => {
    setSelectedFilter('all');
    setSelectedCategory('all');
    setSearchQuery('');
    setDateRange('all');
  };
  const handleCardClick = (card: 'total' | 'urgent' | 'completed' | 'deleted') => {
    if (activeCard === card) {
      // Toggle off — reset to defaults
      setActiveCard(null);
      setViewMode('all');
      resetFilters();
      return;
    }
    setActiveCard(card);
    resetFilters();
    switch (card) {
      case 'total':
        setViewMode('all');
        break;
      case 'urgent':
        setViewMode('all');
        setSelectedFilter('urgent');
        break;
      case 'completed':
        setViewMode('all');
        break;
      case 'deleted':
        setViewMode('all');
        break;
    }
  };
  const getAlertIcon = (type: AlertType) => {
    switch (type) {
      case 'urgent':
        return <AlertTriangle size={20} className="text-[#DB3725]" data-id="element-1272" />;
      case 'warning':
        return <Clock size={20} className="text-yellow-600" data-id="element-1273" />;
      case 'success':
        return <CheckCircle size={20} className="text-[#007558]" data-id="element-1274" />;
      case 'info':
        return <Info size={20} className="text-[#30718D]" data-id="element-1275" />;
    }
  };
  const getAlertColor = (type: AlertType) => {
    switch (type) {
      case 'urgent':
        return 'border-[#DB3725] bg-[#DB3725]/5';
      case 'warning':
        return 'border-yellow-500 bg-white';
      case 'success':
        return 'border-[#007558] bg-[#007558]/5';
      case 'info':
        return 'border-[#30718D] bg-[#30718D]/5';
    }
  };
  const getRecipientName = (alert: Alert) => alert.learnerName || 'Team';
  const getRecipientFirstName = (alert: Alert) => alert.learnerName ? alert.learnerName.split(' ')[0] : 'Team';
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1276">
      {/* Send Message Modal */}
      <AnimatePresence data-id="element-1277">
        {messageModal.isOpen && messageModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMessageModal({
        isOpen: false,
        alert: null
      })} data-id="element-1278">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1279">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1280">
                <div className="flex items-center gap-3" data-id="element-1281">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-1282">
                    <Mail size={24} className="text-white" data-id="element-1283" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1284">
                    Send Message
                  </h2>
                </div>
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1285">
                  <X size={20} className="text-gray-500" data-id="element-1286" />
                </button>
              </div>
              <div className="p-6" data-id="element-1287">
                <p className="text-gray-600 mb-1" data-id="element-1288">
                  Your message has been successfully sent to
                </p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1289">
                  {getRecipientName(messageModal.alert)}
                </p>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1290">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-1291">Message Preview:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1292">
                    "Hi {getRecipientFirstName(messageModal.alert)}, I noticed
                    your recent assessment activity and wanted to reach out to
                    discuss your progress. Let's schedule a time to connect."
                  </p>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1293">
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1294">
                  Cancel
                </button>
                <button onClick={() => setMessageModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1295">
                  <Send size={18} data-id="element-1296" />
                  Send
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Schedule Meeting Modal */}
      <AnimatePresence data-id="element-1297">
        {meetingModal.isOpen && meetingModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setMeetingModal({
        isOpen: false,
        alert: null
      })} data-id="element-1298">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1299">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1300">
                <div className="flex items-center gap-3" data-id="element-1301">
                  <div className="w-12 h-12 rounded-lg bg-[#30718D] flex items-center justify-center" data-id="element-1302">
                    <Video size={24} className="text-white" data-id="element-1303" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1304">
                    Schedule Meeting
                  </h2>
                </div>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1305">
                  <X size={20} className="text-gray-500" data-id="element-1306" />
                </button>
              </div>
              <div className="p-6" data-id="element-1307">
                <p className="text-gray-600 mb-1" data-id="element-1308">Schedule a meeting with</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1309">
                  {getRecipientName(meetingModal.alert)}
                </p>
                <div className="space-y-4" data-id="element-1310">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1311">
                    <p className="text-sm text-gray-500 mb-2" data-id="element-1312">Meeting Topic:</p>
                    <p className="text-gray-700 font-medium" data-id="element-1313">
                      {meetingModal.alert.title}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1314">
                    <div data-id="element-1315">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1316">
                        Date
                      </label>
                      <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue={new Date().toISOString().split('T')[0]} data-id="element-1317" />
                    </div>
                    <div data-id="element-1318">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1319">
                        Time
                      </label>
                      <input type="time" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" defaultValue="10:00" data-id="element-1320" />
                    </div>
                  </div>
                  <div data-id="element-1321">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1322">
                      Duration
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-1323">
                      <option data-id="element-1324">15 minutes</option>
                      <option selected data-id="element-1325">30 minutes</option>
                      <option data-id="element-1326">45 minutes</option>
                      <option data-id="element-1327">1 hour</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1328">
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1329">
                  Cancel
                </button>
                <button onClick={() => setMeetingModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1330">
                  <Calendar size={18} data-id="element-1331" />
                  Schedule
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Send Kudos Modal */}
      <AnimatePresence data-id="element-1332">
        {kudosModal.isOpen && kudosModal.alert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setKudosModal({
        isOpen: false,
        alert: null
      })} data-id="element-1333">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-1334">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-1335">
                <div className="flex items-center gap-3" data-id="element-1336">
                  <div className="w-12 h-12 rounded-lg bg-[#007558] flex items-center justify-center" data-id="element-1337">
                    <ThumbsUp size={24} className="text-white" data-id="element-1338" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1339">
                    Give Kudos
                  </h2>
                </div>
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1340">
                  <X size={20} className="text-gray-500" data-id="element-1341" />
                </button>
              </div>
              <div className="p-6" data-id="element-1342">
                <p className="text-gray-600 mb-1" data-id="element-1343">Send kudos to celebrate</p>
                <p className="text-lg font-bold text-gray-900 mb-6" data-id="element-1344">
                  {getRecipientName(kudosModal.alert)}
                </p>
                <div className="bg-[#007558]/5 rounded-lg p-4 border border-[#007558]/20 mb-4" data-id="element-1345">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1346">
                    <Star size={18} className="text-[#007558]" data-id="element-1347" />
                    <p className="text-sm font-medium text-[#007558]" data-id="element-1348">
                      Achievement:
                    </p>
                  </div>
                  <p className="text-gray-700" data-id="element-1349">{kudosModal.alert.title}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-1350">
                  <p className="text-sm text-gray-500 mb-2" data-id="element-1351">Kudos Message:</p>
                  <p className="text-gray-700 leading-relaxed" data-id="element-1352">
                    "Congratulations {getRecipientFirstName(kudosModal.alert)}!
                    🎉 Your hard work and dedication have really paid off. Keep
                    up the amazing work!"
                  </p>
                </div>
                <div className="mt-4" data-id="element-1353">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1354">
                    Add a personal note (optional)
                  </label>
                  <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007558] focus:border-transparent resize-none" rows={3} placeholder="Add your personal message..." data-id="element-1355" />
                </div>
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-100" data-id="element-1356">
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium" data-id="element-1357">
                  Cancel
                </button>
                <button onClick={() => setKudosModal({
              isOpen: false,
              alert: null
            })} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium" data-id="element-1358">
                  <ThumbsUp size={18} data-id="element-1359" />
                  Send Kudos
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1360">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1361">
          <div className="flex items-center h-16" data-id="element-1362">
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-1363">LOGO</div>

            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1364">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1365">
                <Home size={18} data-id="element-1366" />
                <span className="hidden sm:inline" data-id="element-1367">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1368">
                <BarChart3 size={18} data-id="element-1369" />
                <span className="hidden sm:inline" data-id="element-1370">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1371">
                <FileText size={18} data-id="element-1372" />
                <span className="hidden sm:inline" data-id="element-1373">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1374">
                <UserCog size={18} data-id="element-1375" />
                <span className="hidden sm:inline" data-id="element-1376">Users</span>
              </Link>
            </nav>

            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1377">
              <Link to="/alerts" className="relative p-2 text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-1378">
                <Bell size={20} data-id="element-1379" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1380"></span>}
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1381">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1382">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1383">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1384">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1385" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1386">
                    <div className="py-2" data-id="element-1387">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1388">
                        <Users size={18} className="text-gray-600" data-id="element-1389" />
                        <span className="font-medium text-gray-900" data-id="element-1390">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1391">
                        <Home size={18} className="text-gray-600" data-id="element-1392" />
                        <span className="font-medium text-gray-900" data-id="element-1393">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1394">
                        <UserCog size={18} className="text-gray-600" data-id="element-1395" />
                        <span className="font-medium text-gray-900" data-id="element-1396">
                          Admin Hub
                        </span>
                      </Link>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1397">
                        <SettingsIcon size={18} className="text-gray-600" data-id="element-1398" />
                        <span className="font-medium text-gray-900" data-id="element-1399">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1400"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1401">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1402">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1403"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1404"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1405"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1406">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-1407">
        {/* Header */}
        <div className="mb-8" data-id="element-1408">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-1409">
            <div data-id="element-1410">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-1411">
                Action Items
              </h1>
              <p className="text-gray-600 text-sm sm:text-base" data-id="element-1412">
                Stay updated with important alerts and take action on learner
                needs
              </p>
            </div>
            <button onClick={markAllAsRead} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors self-start" data-id="element-1413">
              <CheckCircle size={16} className="text-[#007558]" data-id="element-1414" />
              Mark all as complete
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4" data-id="element-1415">
            <motion.button onClick={() => handleCardClick('total')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'total' ? 'border-[#30718D] ring-1 ring-[#30718D]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1416">
              <div className="flex items-center justify-between" data-id="element-1417">
                <div data-id="element-1418">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-1419">
                    {alerts.filter(a => !a.completed && !a.deleted).length}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1420">
                    Total Active Alerts
                  </div>
                </div>
                <Bell size={24} className="text-[#30718D]" data-id="element-1421" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('urgent')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.15
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'urgent' ? 'border-[#DB3725] ring-1 ring-[#DB3725]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1422">
              <div className="flex items-center justify-between" data-id="element-1423">
                <div data-id="element-1424">
                  <div className="text-2xl font-bold text-[#DB3725]" data-id="element-1425">
                    {urgentCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1426">Urgent</div>
                </div>
                <AlertTriangle size={24} className="text-[#DB3725]" data-id="element-1427" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('completed')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'completed' ? 'border-[#007558] ring-1 ring-[#007558]/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1428">
              <div className="flex items-center justify-between" data-id="element-1429">
                <div data-id="element-1430">
                  <div className="text-2xl font-bold text-[#007558]" data-id="element-1431">
                    {completedCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1432">Completed</div>
                </div>
                <CheckCircle size={24} className="text-[#007558]" data-id="element-1433" />
              </div>
            </motion.button>

            <motion.button onClick={() => handleCardClick('deleted')} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.25
          }} className={`bg-white rounded-xl p-4 shadow-sm border-2 text-left transition-all ${activeCard === 'deleted' ? 'border-gray-500 ring-1 ring-gray-500/20' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1434">
              <div className="flex items-center justify-between" data-id="element-1435">
                <div data-id="element-1436">
                  <div className="text-2xl font-bold text-gray-500" data-id="element-1437">
                    {deletedCount}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1438">Deleted</div>
                </div>
                <Archive size={24} className="text-gray-500" data-id="element-1439" />
              </div>
            </motion.button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6" data-id="element-1440">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 flex-wrap" data-id="element-1441">
            <div className="flex-1 min-w-0 relative" data-id="element-1442">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1443" />
              <input type="text" placeholder="Search notifications..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm" data-id="element-1444" />
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-1445">
              <Filter size={16} className="text-gray-600" data-id="element-1446" />
              <select value={selectedFilter} onChange={e => setSelectedFilter(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-1447">
                <option value="all" data-id="element-1448">All Types</option>
                <option value="urgent" data-id="element-1449">Urgent</option>
                <option value="warning" data-id="element-1450">Warning</option>
                <option value="success" data-id="element-1451">Success</option>
                <option value="info" data-id="element-1452">Info</option>
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-1453">
              <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-1454">
                <option value="all" data-id="element-1455">All Categories</option>
                <option value="learner" data-id="element-1456">Learner Alerts</option>
                <option value="system" data-id="element-1457">System Alerts</option>
                <option value="achievement" data-id="element-1458">Kudos</option>
                <option value="completed" data-id="element-1459">Completed</option>
                <option value="deleted" data-id="element-1460">Deleted</option>
              </select>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-id="element-1461">
              <Calendar size={16} className="text-gray-600" data-id="element-1462" />
              <select value={dateRange} onChange={e => setDateRange(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent flex-1 sm:flex-none" data-id="element-1463">
                <option value="all" data-id="element-1464">All Time</option>
                <option value="today" data-id="element-1465">Today</option>
                <option value="7days" data-id="element-1466">Last 7 Days</option>
                <option value="30days" data-id="element-1467">Last 30 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Alerts List */}
        <div className="space-y-3" data-id="element-1468">
          <AnimatePresence mode="popLayout" data-id="element-1469">
            {filteredAlerts.map((alert, index) => <motion.div key={alert.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            delay: index * 0.05
          }} className={`bg-white rounded-xl shadow-sm border-l-4 overflow-hidden ${getAlertColor(alert.type)} ${!alert.read ? 'border-l-4' : 'border-l-2 opacity-75'}`} data-id="element-1470">
                <div className="p-4 sm:p-6" data-id="element-1471">
                  <div className="flex items-start gap-3 sm:gap-4" data-id="element-1472">
                    <div className="flex-shrink-0 mt-1" data-id="element-1473">
                      {getAlertIcon(alert.type)}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-1474">
                      {/* Title and Actions - Stack on mobile */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-2" data-id="element-1475">
                        <div className="flex-1" data-id="element-1476">
                          <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-1477">
                            <h3 className="text-sm sm:text-base font-bold text-gray-900" data-id="element-1478">
                              {alert.title}
                            </h3>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed" data-id="element-1479">
                            {alert.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1480">
                          {!alert.completed && !alert.deleted && <>
                              {alert.category !== 'system' && <button onClick={() => completeAlert(alert.id)} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" title="Mark as completed" data-id="element-1481">
                                  <CheckCircle size={14} className="text-[#007558]" data-id="element-1482" />
                                  <span className="hidden sm:inline" data-id="element-1483">
                                    Mark Completed
                                  </span>
                                </button>}
                              <button onClick={() => softDeleteAlert(alert.id)} className="p-1.5 text-gray-400 hover:text-[#DB3725] hover:bg-[#DB3725]/10 rounded-lg transition-colors" title="Delete" data-id="element-1484">
                                <X size={16} data-id="element-1485" />
                              </button>
                            </>}
                          {alert.completed && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium bg-[#007558]/10 text-[#007558] rounded-lg" data-id="element-1486">
                              <CheckCircle size={14} data-id="element-1487" />
                              <span className="hidden sm:inline" data-id="element-1488">
                                Completed
                              </span>
                            </span>}
                          {alert.deleted && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium bg-gray-100 text-gray-500 rounded-lg" data-id="element-1489">
                              <Archive size={14} data-id="element-1490" />
                              <span className="hidden sm:inline" data-id="element-1491">Deleted</span>
                            </span>}
                        </div>
                      </div>

                      {/* Learner Info - Stack on mobile */}
                      {alert.learnerName && <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mb-3 text-sm" data-id="element-1492">
                          <div className="flex items-center gap-2" data-id="element-1493">
                            <Users size={14} className="text-gray-500" data-id="element-1494" />
                            <span className="font-medium text-gray-700" data-id="element-1495">
                              {alert.learnerName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-1496">
                            <Mail size={14} className="text-gray-500" data-id="element-1497" />
                            <span className="text-gray-600 text-xs sm:text-sm" data-id="element-1498">
                              {alert.learnerEmail}
                            </span>
                          </div>
                          {alert.module && <div className="flex items-center gap-2" data-id="element-1499">
                              <FileBarChart size={14} className="text-gray-500" data-id="element-1500" />
                              <span className="text-gray-600 text-xs sm:text-sm" data-id="element-1501">
                                {alert.module}
                              </span>
                            </div>}
                        </div>}

                      {/* Action Buttons and Timestamp */}
                      <div className="pt-3 border-t border-gray-200" data-id="element-1502">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-1503">
                          {alert.actionable ? <div className="flex flex-wrap items-center gap-2" data-id="element-1504">
                              <button onClick={() => setMessageModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors" data-id="element-1505">
                                <Mail size={14} data-id="element-1506" />
                                <span className="hidden sm:inline" data-id="element-1507">
                                  Send Message
                                </span>
                              </button>
                              <button onClick={() => setMeetingModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1508">
                                <Video size={14} data-id="element-1509" />
                                <span className="hidden sm:inline" data-id="element-1510">
                                  Schedule Meeting
                                </span>
                              </button>
                              {alert.type === 'success' && <button onClick={() => setKudosModal({
                          isOpen: true,
                          alert
                        })} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium border border-[#007558] bg-[#007558]/10 text-[#007558] rounded-lg hover:bg-[#007558]/20 transition-colors" data-id="element-1511">
                                  <ThumbsUp size={14} data-id="element-1512" />
                                  <span className="hidden sm:inline" data-id="element-1513">
                                    Send Kudos
                                  </span>
                                </button>}
                            </div> : <div data-id="element-1514" />}
                          <span className="flex items-center gap-1.5 text-xs text-gray-400 self-start sm:self-auto" data-id="element-1515">
                            <Clock size={12} data-id="element-1516" />
                            {alert.timestamp}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </AnimatePresence>

          {filteredAlerts.length === 0 && <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200" data-id="element-1517">
              {activeCard === 'completed' ? <>
                  <CheckCircle size={48} className="mx-auto text-[#007558] mb-4" data-id="element-1518" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1519">
                    No completed alerts
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1520">
                    Alerts you mark as completed will appear here.
                  </p>
                  <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-1521">
                    <Bell size={16} data-id="element-1522" />
                    View all alerts
                  </button>
                </> : activeCard === 'deleted' ? <>
                  <Archive size={48} className="mx-auto text-gray-400 mb-4" data-id="element-1523" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1524">
                    No deleted alerts
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1525">
                    Alerts you delete will appear here.
                  </p>
                  <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-1526">
                    <Bell size={16} data-id="element-1527" />
                    View all alerts
                  </button>
                </> : <>
                  <Bell size={48} className="mx-auto text-gray-400 mb-4" data-id="element-1528" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1529">
                    No notifications found
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1530">
                    Try adjusting your filters or search query
                  </p>
                  {(selectedFilter !== 'all' || selectedCategory !== 'all' || dateRange !== 'all' || searchQuery || activeCard) && <button onClick={() => {
              setActiveCard(null);
              setViewMode('all');
              resetFilters();
            }} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors" data-id="element-1531">
                      <X size={16} data-id="element-1532" />
                      Clear all filters
                    </button>}
                </>}
            </div>}
        </div>
      </div>
    </div>;
}